import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ViewerTrackingOptions {
  streamId?: string;
  pageVisited: string;
  matchNumber?: number;
}

// Session ID for live online tracking (persists across page navigation within session)
const getViewerSessionId = (): string => {
  let sessionId = sessionStorage.getItem('gs_viewer_session');
  if (!sessionId) {
    sessionId = `v-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('gs_viewer_session', sessionId);
  }
  return sessionId;
};

// Generate browser fingerprint for guest tracking
const generateFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('GS Fingerprint', 2, 2);
  }
  const canvasFingerprint = canvas.toDataURL().slice(-50);
  
  const screenData = `${screen.width}x${screen.height}x${screen.colorDepth}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = navigator.language;
  const platform = navigator.platform || 'unknown';
  
  const raw = `${canvasFingerprint}-${screenData}-${timezone}-${language}-${platform}`;
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const char = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `fp-${Math.abs(hash).toString(36)}`;
};

// Cached visitor info to avoid repeated API calls
let cachedIpAddress: string | null = null;
let cachedCountry: string | null = null;
let ipFetchPromise: Promise<void> | null = null;
let cachedFingerprint: string | null = null;

const fetchIpInfo = async (): Promise<void> => {
  if (cachedIpAddress) return;
  if (ipFetchPromise) return ipFetchPromise;
  
  ipFetchPromise = (async () => {
    try {
      // Use multiple fallback IP services for reliability
      let ipData: any = null;
      
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
        ipData = await ipRes.json();
        cachedIpAddress = ipData.ip;
      } catch {
        try {
          const ipRes2 = await fetch('https://api.ip.sb/ip', { signal: AbortSignal.timeout(3000) });
          cachedIpAddress = (await ipRes2.text()).trim();
        } catch {
          cachedIpAddress = 'unknown';
        }
      }
      
      // Fetch country with fallback
      if (cachedIpAddress && cachedIpAddress !== 'unknown') {
        try {
          const geoRes = await fetch(`https://ipapi.co/${cachedIpAddress}/json/`, { signal: AbortSignal.timeout(3000) });
          const geoData = await geoRes.json();
          cachedCountry = geoData.country_code || null;
        } catch {
          try {
            const geoRes2 = await fetch(`https://ip-api.com/json/${cachedIpAddress}`, { signal: AbortSignal.timeout(3000) });
            const geoData2 = await geoRes2.json();
            cachedCountry = geoData2.countryCode || null;
          } catch {
            cachedCountry = null;
          }
        }
      }
    } catch (e) {
      console.log('Could not fetch IP/geo info');
      cachedIpAddress = 'unknown';
    }
  })();
  
  return ipFetchPromise;
};

// Import useAuth directly - require() doesn't work in Vite/ESM
import { useAuth } from '@/contexts/AuthContext';

const useSafeAuth = () => {
  try {
    return useAuth();
  } catch {
    return { user: null, profile: null, roles: [] };
  }
};

export function useViewerTracking({ streamId, pageVisited, matchNumber }: ViewerTrackingOptions) {
  const authData = useSafeAuth();
  const user = authData?.user || null;
  const profile = authData?.profile || null;
  const roles = authData?.roles || [];
  
  const activityIdRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevStreamIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string>(getViewerSessionId());
  const fingerprintRef = useRef<string>('');
  const watchTimeStartRef = useRef<number | null>(null);
  const totalWatchTimeRef = useRef<number>(0);

  useEffect(() => {
    // Generate fingerprint once
    if (!fingerprintRef.current) {
      fingerprintRef.current = generateFingerprint();
      cachedFingerprint = fingerprintRef.current;
    }

    const getVisitorInfo = () => {
      const ua = navigator.userAgent;
      const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
      
      let browser = 'Unknown';
      if (ua.includes('Firefox')) browser = 'Firefox';
      else if (ua.includes('Edg')) browser = 'Edge';
      else if (ua.includes('Chrome')) browser = 'Chrome';
      else if (ua.includes('Safari')) browser = 'Safari';
      else if (ua.includes('Opera')) browser = 'Opera';

      return { browser, device_type: isMobile ? 'mobile' : 'desktop' };
    };

    const getUserRole = () => {
      if (!user) return 'guest';
      if (!roles || roles.length === 0) return 'user';
      const roleNames = roles.map((r: any) => typeof r === 'string' ? r : r?.role || r);
      if (roleNames.includes('admin')) return 'admin';
      if (roleNames.includes('moderator')) return 'moderator';
      if (roleNames.includes('streamer')) return 'streamer';
      if (roleNames.includes('affiliate')) return 'affiliate';
      return 'user';
    };

    const updateStreamViewerCount = async (sId: string, increment: boolean) => {
      try {
        const sessionId = sessionIdRef.current;
        
        // Use the secure RPC function instead of direct update
        const { error } = await supabase.rpc('update_stream_viewer_count', {
          _stream_id: sId,
          _increment: increment,
          _session_id: sessionId
        });
        
        if (error) {
          // Fallback: If RPC fails (e.g., session not yet logged), silently ignore
          // The viewer count will be updated on next heartbeat when session exists
          console.log('Viewer count update deferred:', error.message);
        }

        // Track watch time start when incrementing (starting to watch)
        if (increment) {
          watchTimeStartRef.current = Date.now();
          totalWatchTimeRef.current = 0;
        }
      } catch (e) {
        console.log('Error updating viewer count');
      }
    };

    // Update watch time for streamer analytics
    const updateWatchTime = async (sId: string) => {
      if (!watchTimeStartRef.current) return;
      
      const sessionId = sessionIdRef.current;
      const elapsedSeconds = Math.floor((Date.now() - watchTimeStartRef.current) / 1000);
      const incrementSeconds = elapsedSeconds - totalWatchTimeRef.current;
      
      if (incrementSeconds <= 0) return;
      
      totalWatchTimeRef.current = elapsedSeconds;

      try {
        await supabase.rpc('update_streamer_watch_time', {
          _session_id: sessionId,
          _stream_id: sId,
          _watch_seconds: incrementSeconds
        });
      } catch (e) {
        // Silently ignore watch time update errors
      }
    };

    // Format page visited to use match_number if available
    const formatPageVisited = () => {
      if (matchNumber && pageVisited.includes('/match/')) {
        return `/match/${matchNumber}`;
      }
      return pageVisited;
    };

    // Get affiliate code from URL or session
    const getAffiliateCode = (): string | null => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlCode = urlParams.get('ref') || urlParams.get('aff') || null;
      if (urlCode) {
        try {
          sessionStorage.setItem('affiliate_code', urlCode);
        } catch (e) {}
        return urlCode;
      }
      try {
        return sessionStorage.getItem('affiliate_code');
      } catch (e) {
        return null;
      }
    };

    // Resolve affiliate code to link ID using database function
    const resolveAffiliateLink = async (code: string | null): Promise<string | null> => {
      if (!code) return null;
      try {
        const { data, error } = await supabase.rpc('resolve_affiliate_code', { _code: code });
        if (error || !data) return null;
        return data;
      } catch (e) {
        return null;
      }
    };

    // Create unique visitor key for deduplication
    const createVisitorKey = (ip: string, fingerprint: string): string => {
      return `${ip}-${fingerprint}`;
    };

    const logActivity = async () => {
      const { browser, device_type } = getVisitorInfo();
      const sessionId = sessionIdRef.current;
      const fingerprint = fingerprintRef.current;
      
      try {
        // Fetch IP info (cached after first call)
        await fetchIpInfo();
        const ip_address = cachedIpAddress || 'unknown';
        const country = cachedCountry;
        const visitorKey = createVisitorKey(ip_address, fingerprint);

        // Get affiliate link ID if visiting via affiliate link
        const affiliateCode = getAffiliateCode();
        const affiliateLinkId = await resolveAffiliateLink(affiliateCode);

        const formattedPage = formatPageVisited();
        const now = new Date().toISOString();

        // Try to find existing activity record by session_id first
        const { data: existingBySession } = await supabase
          .from('user_activity_logs')
          .select('id')
          .eq('session_id', sessionId)
          .limit(1)
          .maybeSingle();

        if (existingBySession) {
          // Update existing record
          activityIdRef.current = existingBySession.id;
          
          const { error: updateError } = await supabase
            .from('user_activity_logs')
            .update({
              page_visited: formattedPage,
              user_email: profile?.email || user?.email || null,
              user_role: getUserRole(),
              is_watching_stream: !!streamId,
              stream_id: streamId || null,
              last_heartbeat: now,
              affiliate_link_id: affiliateLinkId,
              user_id: user?.id || null,
              ip_address,
              country,
              browser,
              device_type,
            })
            .eq('id', existingBySession.id);

          // Silently handle update errors - analytics should not log to console

          // Update stream viewer counts
          if (streamId && streamId !== prevStreamIdRef.current) {
            if (prevStreamIdRef.current) {
              await updateStreamViewerCount(prevStreamIdRef.current, false);
            }
            await updateStreamViewerCount(streamId, true);
            prevStreamIdRef.current = streamId;
          }
          
          return;
        }

        // Check if there's a duplicate by IP + fingerprint (same visitor with different session)
        // Only keep the most recent one active
        const thirtySecondsAgo = new Date(Date.now() - 30000).toISOString();
        const { data: existingByVisitor } = await supabase
          .from('user_activity_logs')
          .select('id, session_id')
          .eq('ip_address', ip_address)
          .gte('last_heartbeat', thirtySecondsAgo)
          .neq('session_id', sessionId)
          .limit(1)
          .maybeSingle();

        // If duplicate exists, mark it as inactive by not updating it anymore
        // The new session will take over

        // No existing record, create new one
        const { data, error } = await supabase
          .from('user_activity_logs')
          .insert({
            user_id: user?.id || null,
            session_id: sessionId,
            page_visited: formattedPage,
            ip_address,
            country,
            browser,
            device_type,
            referrer: document.referrer || null,
            user_email: profile?.email || user?.email || null,
            user_role: getUserRole(),
            is_watching_stream: !!streamId,
            stream_id: streamId || null,
            last_heartbeat: now,
            affiliate_link_id: affiliateLinkId,
          })
          .select('id')
          .single();

        // Silently handle insert errors - analytics should not log to console
        if (error) return;

        if (data) {
          activityIdRef.current = data.id;

          // Update stream viewer counts
          if (streamId && streamId !== prevStreamIdRef.current) {
            if (prevStreamIdRef.current) {
              await updateStreamViewerCount(prevStreamIdRef.current, false);
            }
            await updateStreamViewerCount(streamId, true);
            prevStreamIdRef.current = streamId;
          }
        }
      } catch {
        // Silently handle errors - analytics should not log to console
      }
    };

    // Mark user as offline using left_at (actual leave event, not TTL-based)
    const markOffline = () => {
      if (activityIdRef.current) {
        const now = new Date().toISOString();
        
        // Use sendBeacon for reliable delivery during page unload
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_activity_logs?id=eq.${activityIdRef.current}`;
        const body = JSON.stringify({
          left_at: now,
          is_watching_stream: false,
          stream_id: null,
        });
        
        const sent = navigator.sendBeacon?.(
          url,
          new Blob([body], { type: 'application/json' })
        );
        
        // sendBeacon doesn't support custom headers, use keepalive fetch as primary
        try {
          fetch(url, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
              'Prefer': 'return=minimal',
            },
            body,
            keepalive: true,
          }).catch(() => {});
        } catch {
          // Ignore errors during unload
        }
      }
    };

    // Handle page visibility change
    const handleVisibilityChange = () => {
      if (activityIdRef.current) {
        if (document.visibilityState === 'hidden') {
          // Tab hidden - mark as left
          markOffline();
        } else {
          // Tab visible again - clear left_at and refresh heartbeat
          supabase
            .from('user_activity_logs')
            .update({ 
              left_at: null,
              last_heartbeat: new Date().toISOString(),
              is_watching_stream: !!streamId,
              stream_id: streamId || null,
            })
            .eq('id', activityIdRef.current)
            .then(() => {});
        }
      }
    };

    // Handle before unload (browser/tab close, navigation away)
    const handleBeforeUnload = () => {
      markOffline();
    };

    logActivity();

    // Heartbeat updates existing record every 5 seconds
    // This ensures users stay visible with 5s online threshold for real-time accuracy
    heartbeatIntervalRef.current = setInterval(async () => {
      if (activityIdRef.current) {
        const formattedPage = formatPageVisited();
        const { browser, device_type } = getVisitorInfo();
        
        // Also update IP and country on heartbeat in case it changed
        await fetchIpInfo();
        
        await supabase
          .from('user_activity_logs')
          .update({ 
            last_heartbeat: new Date().toISOString(),
            left_at: null, // Clear left_at on every heartbeat - user is still here
            page_visited: formattedPage,
            is_watching_stream: !!streamId,
            stream_id: streamId || null,
            user_email: profile?.email || user?.email || null,
            user_role: getUserRole(),
            ip_address: cachedIpAddress || 'unknown',
            country: cachedCountry,
            browser,
            device_type,
          })
          .eq('id', activityIdRef.current);

        // Update watch time for streamer analytics (every 5 seconds)
        if (streamId) {
          await updateWatchTime(streamId);
        }
      }
    }, 5000); // 5 second heartbeat for more responsive tracking

    // Add event listeners for immediate offline detection
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Clean up event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (prevStreamIdRef.current) {
        updateStreamViewerCount(prevStreamIdRef.current, false);
      }
      // IMPORTANT: Do NOT mark offline on unmount!
      // This would incorrectly remove users during normal React re-renders,
      // stream switching, or match page navigation.
      // Only beforeunload (tab/browser close) should mark user offline.
      // The heartbeat expiry (30 seconds threshold) handles natural cleanup.
    };
  }, [user, profile, roles, streamId, pageVisited, matchNumber]);
}
